import { add, addBusinessDays as fnsAddBusinessDays, addHours as fnsAddHours, differenceInDays as fnsDifferenceInDays, endOfDay as fnsEndOfDay, endOfMonth as fnsEndOfMonth, getDate as fnsGetDate, getDay as fnsGetDay, getHours as fnsGetHours, getMinutes as fnsGetMinutes, getMonth as fnsGetMonth, getTime as fnsGetTime, getYear as fnsGetYear, isDate as fnsIsDate, isPast as fnsIsPast, isToday as fnsIsToday, isThisMonth as fnsIsThisMonth, lastDayOfMonth as fnsLastDayOfMonth, nextDay as fnsNextDay, previousDay as fnsPreviousDay, setDate as fnsSetDate, startOfDay as fnsStartOfDay, startOfMonth as fnsStartOfMonth, subBusinessDays as fnsSubBusinessDays, format, formatDistanceStrict, formatDistanceToNow, formatISO, isWeekend, parse, parseISO, subDays, subHours, subMilliseconds, subMinutes, subMonths, subYears, toDate } from 'date-fns';
import { MONTHNAME_DAY, MONTHNAME_PADDEDDAY_YEAR, MONTH_DAY_YEAR } from '../constants/abacus_time_formatter_strings';
const WEEKDAY_TO_NUMBER_MAPPING = {
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
    SUNDAY: 0
};
/**
 * converts date to a provided format
 *
 * @param {*} date
 * @param {string} [format='MMM dd, yyyy']
 * @param {boolean}
 * @return Date
 */
function formatDate(date, dateFormat = MONTHNAME_PADDEDDAY_YEAR) {
    try {
        if (typeof date === 'string') {
            return format(parseISO(date), dateFormat);
        }
        const res = format(date, dateFormat);
        return res;
    }
    catch (e) {
        return e.message;
    }
}
/**
 * Given a date and an optional dateFormat
 * @param date
 * @param dateFormat
 * @returns
 */
function toDateObject(date, dateFormat = MONTHNAME_PADDEDDAY_YEAR) {
    try {
        if (typeof date === 'string') {
            const dateInMs = parse(date, dateFormat, new Date());
            const parsedDate = toDate(dateInMs);
            return isValidDate(parsedDate) ? parsedDate : new Date(date);
        }
        return toDate(date);
    }
    catch (e) {
        return e.message;
    }
}
/**
 * refined variant of datefns isDate, tests if the object is a date object and if the object's .valueOf returns a number value (invalid Date is a date object with no ms value)
 * @param date
 * @returns
 */
function isValidDate(date) {
    return fnsIsDate(date) && isNaN(date.valueOf()) === false;
}
/**
 * Returns midnight of the date passed (removes the hrs, mins, sec, and ms from the time)
 * @param d Starting date
 * @returns The time at midnight for a particular day
 */
function getZeroTimeDate(d) {
    return new Date(d.setHours(0, 0, 0, 0));
}
function getNoonDate(date) {
    const zeroedDate = getZeroTimeDate(date);
    return new Date(zeroedDate.setHours(12));
}
function getNoonTimestamp(date) {
    return Date.UTC(getNoonDate(date).getFullYear(), getNoonDate(date).getMonth(), getNoonDate(date).getDate(), getNoonDate(date).getHours());
}
function addDays(d, days) {
    return add(d, { days: days });
}
function addMonths(d, months) {
    return add(d, { months: months });
}
function isToday(d) {
    return fnsIsToday(d);
}
function isThisMonth(d) {
    return fnsIsThisMonth(d);
}
function formatTimeAway(d) {
    return formatDistanceToNow(typeof d === 'string' ? parseISO(d) : d, { addSuffix: true });
}
function formatTimeDistanceStrict(date1, date2, opts) {
    const convertedDate1 = typeof date1 === 'string' ? parseISO(date1) : date1;
    const convertedDate2 = typeof date2 === 'string' ? parseISO(date2) : date2;
    return formatDistanceStrict(convertedDate1, convertedDate2, opts);
}
const formatTimeAgo = formatTimeAway;
const formatTimeFromNow = formatTimeAway;
function subtractMs(d, minutes) {
    return subMilliseconds(d, minutes);
}
function subtractMinutes(d, minutes) {
    return subMinutes(d, minutes);
}
function subtractHours(d, hours) {
    return subHours(d, hours);
}
function subtractDays(d, days) {
    return subDays(d, days);
}
function subtractMonths(d, months) {
    return subMonths(d, months);
}
function subtractYears(d, years) {
    return subYears(d, years);
}
/**
 * Returns the formatted date
 *
 * Given the current date is 2022-02-02
 * If the date is 2022-01-12 => returns Jan 12
 * If the date is 2021-01-12 => returns 01/12/2021
 *
 */
function formatDateOnly(date) {
    const today = new Date();
    if (date.getUTCFullYear() === today.getUTCFullYear()) {
        return formatDate(date, MONTHNAME_DAY);
    }
    return formatDate(date, MONTH_DAY_YEAR.RAW);
}
function getCurrentYear() {
    return new Date().getFullYear();
}
function getDate(date) {
    return fnsGetDate(date);
}
function getMonth(date) {
    return fnsGetMonth(date);
}
function getYear(date) {
    return fnsGetYear(date);
}
function getHours(date) {
    return fnsGetHours(date);
}
function getMinutes(date) {
    return fnsGetMinutes(date);
}
function addHours(date, hours) {
    return fnsAddHours(date, hours);
}
function getDay(date) {
    return fnsGetDay(date);
}
function differenceInDays(date1, date2) {
    return fnsDifferenceInDays(date1, date2);
}
function startOfMonth(date) {
    return fnsStartOfMonth(date);
}
function endOfMonth(date) {
    return fnsEndOfMonth(date);
}
function startOfDay(date) {
    return fnsStartOfDay(date);
}
function endOfDay(date) {
    return fnsEndOfDay(date);
}
function getTime(date) {
    return fnsGetTime;
}
function nextDay(date, weekday) {
    return fnsNextDay(date, weekday);
}
function previousDay(date, weekday) {
    return fnsPreviousDay(date, weekday);
}
function isPast(date) {
    return fnsIsPast(date);
}
function setDate(date, dayOfMonth) {
    return fnsSetDate(date, dayOfMonth);
}
function lastDayOfMonth(date) {
    return fnsLastDayOfMonth(date);
}
function setMonthDateWithoutOverflow(date, dayNumber) {
    const clonedDate = new Date(date.getTime());
    let returnDate = setDate(clonedDate, dayNumber);
    // we have overflow
    if (getMonth(returnDate) !== getMonth(clonedDate)) {
        returnDate = lastDayOfMonth(clonedDate);
    }
    return returnDate;
}
/*
  Get the last dayNumber of the month. eg getLastNumberDateOfMonth(5, new Date('2023-02-25')) will return Feb 5, 2023,
  while getLastNumberDateOfMonth(5, new Date('2023-02-02')) will return Jan 5, 2023
*/
function getLastCycleByDate(dayNumber, currentTimeInMillis = new Date().getTime()) {
    const currentDate = new Date(currentTimeInMillis);
    let calculatedDate;
    if (getDate(currentDate) > dayNumber) {
        calculatedDate = setDate(currentDate, dayNumber);
    }
    else {
        const pastMonth = subMonths(currentDate, 1);
        calculatedDate = setMonthDateWithoutOverflow(pastMonth, dayNumber);
    }
    return startOfDay(calculatedDate);
}
function getStatementCycleDates(cycleDate, currentTimeInMillis = new Date().getTime()) {
    const statementDates = {};
    let endDate = getLastCycleByDate(cycleDate, currentTimeInMillis);
    let startDate = getLastCycleByDate(cycleDate, endDate.getTime());
    statementDates.endDate = endOfDay(endDate).getTime();
    statementDates.startDate = startOfDay(addDays(startDate, 1)).getTime();
    return statementDates;
}
function isWeekendDate(date) {
    return isWeekend(date);
}
function addBusinessDays(date, days) {
    return fnsAddBusinessDays(date, days);
}
function subtractBusinessDays(date, days) {
    return fnsSubBusinessDays(date, days);
}
function getTimesFromPeriod(dateRange, offset = 100000) {
    return {
        within: [dateRange.startDate + 1, dateRange.startDate + offset, dateRange.endDate - offset, dateRange.endDate - 1],
        outside: [dateRange.startDate - 1, dateRange.startDate - offset, dateRange.endDate + offset, dateRange.endDate + 1]
    };
}
export { formatISO, formatDate, formatDateOnly, addMonths, addDays, isToday, formatTimeAgo, formatTimeFromNow, getZeroTimeDate, getNoonDate, getNoonTimestamp, subtractMs, subtractMinutes, subtractHours, subtractDays, toDateObject, isValidDate, formatTimeDistanceStrict, getCurrentYear, subtractMonths, subtractYears, getDate, getMonth, getYear, differenceInDays, startOfMonth, endOfMonth, startOfDay, endOfDay, getTime, nextDay, previousDay, WEEKDAY_TO_NUMBER_MAPPING, isPast, isThisMonth, getLastCycleByDate, getStatementCycleDates, setDate, setMonthDateWithoutOverflow, getDay, getHours, isWeekendDate, addHours, addBusinessDays, subtractBusinessDays, getTimesFromPeriod };
